import { useCallback, useEffect, useRef, useState } from 'react';
import {
  voiceAgentApi,
  VoiceAgentSessionMetrics,
  VoiceAgentStartRequest,
} from '../../../api/voiceAgentApi';
import { interviewApi } from '../../../api/interviewApi';
import { API_CONFIG, VOICE_AGENT_CONFIG } from '../../../utils/constants';
import { PCMStreamPlayer } from '../audio/pcmStreamPlayer';
import { PCM_WORKLET_SOURCE } from '../audio/pcmWorkletSource';
import { appendTranscriptTurn } from '../utils/mergeTranscriptTurn';

export type VoiceAgentConnectionState = 'idle' | 'connecting' | 'connected' | 'error';
export type VoiceAgentAgentState = 'idle' | 'listening' | 'thinking' | 'speaking';
export type VoiceAgentPhase = 'idle' | 'active' | 'finalizing' | 'feedback_ready' | 'error';

export interface VoiceAgentTranscriptTurn {
  id: string;
  role: 'agent' | 'user';
  text: string;
}

export interface VoiceAgentSessionEnded {
  reason: string;
  message?: string;
  question_count?: number;
  warning_count?: number;
  feedback?: Record<string, unknown>;
  conversation_history?: Array<{ role: string; content: string }>;
}

export interface VoiceAgentConnectParams {
  sessionId: string;
  wsPath: string;
  firstQuestion?: string;
  maxQuestions?: number;
  dbSessionId?: string | null;
}

const SILENCE_FRAME_BYTES =
  Math.round(VOICE_AGENT_CONFIG.MIC_SAMPLE_RATE * (VOICE_AGENT_CONFIG.SILENCE_FRAME_MS / 1000)) * 2;

const DRAIN_TIMEOUT_MS = 10_000;
/** Safety fallback only — primary finalize path is SessionEnded after agent closure audio. */
const SESSION_END_FALLBACK_MS = 120_000;
const FEEDBACK_POLL_RETRIES = 3;
const FEEDBACK_POLL_INTERVAL_MS = 2_000;
const WAVEFORM_BAR_COUNT = 14;
const IDLE_AUDIO_LEVELS = Array.from({ length: WAVEFORM_BAR_COUNT }, () => 0.08);

function buildVoiceAgentWsUrl(wsPath: string, token: string): string {
  const httpBase = API_CONFIG.INTERVIEW_API_URL.replace(/\/$/, '');
  const wsBase = httpBase.replace(/^http/, 'ws');
  const separator = wsPath.includes('?') ? '&' : '?';
  return `${wsBase}${wsPath}${separator}token=${encodeURIComponent(token)}`;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

interface PolledSessionFeedback {
  feedback?: Record<string, unknown>;
  conversation_history?: Array<{ role: string; content: string }>;
}

async function pollSessionFeedback(dbSessionId: string): Promise<PolledSessionFeedback | undefined> {
  for (let attempt = 0; attempt < FEEDBACK_POLL_RETRIES; attempt += 1) {
    try {
      const fbResult = await interviewApi.getSessionFeedback(dbSessionId);
      const session = fbResult?.session;
      const feedback = session?.detailed_feedback ?? session?.feedback;
      if (feedback) {
        return {
          feedback: feedback as Record<string, unknown>,
          conversation_history: session?.conversation_history,
        };
      }
      if (fbResult?.status === 'pending') {
        await sleep(FEEDBACK_POLL_INTERVAL_MS);
        continue;
      }
    } catch {
      // retry
    }
    if (attempt < FEEDBACK_POLL_RETRIES - 1) {
      await sleep(FEEDBACK_POLL_INTERVAL_MS);
    }
  }
  return undefined;
}

export function useVoiceAgent() {
  const [connectionState, setConnectionState] = useState<VoiceAgentConnectionState>('idle');
  const [agentState, setAgentState] = useState<VoiceAgentAgentState>('idle');
  const [phase, setPhase] = useState<VoiceAgentPhase>('idle');
  const [transcript, setTranscript] = useState<VoiceAgentTranscriptTurn[]>([]);
  const [sessionMetrics, setSessionMetrics] = useState<VoiceAgentSessionMetrics | null>(null);
  const [sessionEnded, setSessionEnded] = useState<VoiceAgentSessionEnded | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [dbSessionId, setDbSessionId] = useState<string | null>(null);
  const [maxQuestions, setMaxQuestions] = useState<number>(7);
  const [audioLevels, setAudioLevels] = useState<number[]>(IDLE_AUDIO_LEVELS);

  const wsRef = useRef<WebSocket | null>(null);
  const sessionIdRef = useRef<string | null>(null);
  const dbSessionIdRef = useRef<string | null>(null);
  const finalizeInProgressRef = useRef(false);
  const sessionFinalizedRef = useRef(false);
  const connectionWasActiveRef = useRef(false);
  const endBackupTimerRef = useRef<number | null>(null);
  const finalizeSessionRef = useRef<
    (reason: string, endMessage?: string, questionCount?: number, warningCount?: number) => Promise<void>
  >(async () => {});
  const agentStateRef = useRef<VoiceAgentAgentState>('idle');
  const inputOpenRef = useRef(true);
  const micOpenTimerRef = useRef<number | null>(null);
  const silencePumpRef = useRef<number | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const micStreamRef = useRef<MediaStream | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const levelsRafRef = useRef<number | null>(null);
  const playerRef = useRef<PCMStreamPlayer | null>(null);
  const connectionStateRef = useRef<VoiceAgentConnectionState>('idle');
  const connectInFlightRef = useRef(false);

  useEffect(() => {
    connectionStateRef.current = connectionState;
  }, [connectionState]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  useEffect(() => {
    dbSessionIdRef.current = dbSessionId;
  }, [dbSessionId]);

  const clearEndBackupTimer = useCallback(() => {
    if (endBackupTimerRef.current !== null) {
      clearTimeout(endBackupTimerRef.current);
      endBackupTimerRef.current = null;
    }
  }, []);

  const clearMicOpenTimer = useCallback(() => {
    if (micOpenTimerRef.current !== null) {
      clearTimeout(micOpenTimerRef.current);
      micOpenTimerRef.current = null;
    }
  }, []);

  const stopSilencePump = useCallback(() => {
    if (silencePumpRef.current !== null) {
      clearInterval(silencePumpRef.current);
      silencePumpRef.current = null;
    }
  }, []);

  const startSilencePump = useCallback(() => {
    if (silencePumpRef.current !== null) return;
    silencePumpRef.current = window.setInterval(() => {
      const ws = wsRef.current;
      if (!ws || ws.readyState !== WebSocket.OPEN || inputOpenRef.current) return;
      ws.send(new ArrayBuffer(SILENCE_FRAME_BYTES));
    }, VOICE_AGENT_CONFIG.SILENCE_FRAME_MS);
  }, []);

  const appendTranscript = useCallback((role: 'agent' | 'user', text: string) => {
    setTranscript((prev) => appendTranscriptTurn(prev, role, text));
  }, []);

  const setInputOpen = useCallback(
    (open: boolean) => {
      inputOpenRef.current = open;
      micStreamRef.current?.getAudioTracks().forEach((track) => {
        track.enabled = open;
      });
      if (open) {
        stopSilencePump();
        agentStateRef.current = 'listening';
        setAgentState('listening');
      } else {
        startSilencePump();
        if (agentStateRef.current === 'listening') {
          agentStateRef.current = 'speaking';
          setAgentState('speaking');
        }
      }
    },
    [startSilencePump, stopSilencePump]
  );

  const scheduleInputOpen = useCallback(() => {
    if (finalizeInProgressRef.current) return;
    clearMicOpenTimer();
    const tryOpen = () => {
      if (finalizeInProgressRef.current) return;
      if (playerRef.current?.hasPendingAudio()) {
        micOpenTimerRef.current = window.setTimeout(tryOpen, 50);
        return;
      }
      setInputOpen(true);
    };
    tryOpen();
  }, [clearMicOpenTimer, setInputOpen]);

  const stopLevelsLoop = useCallback(() => {
    if (levelsRafRef.current !== null) {
      cancelAnimationFrame(levelsRafRef.current);
      levelsRafRef.current = null;
    }
    analyserRef.current?.disconnect();
    analyserRef.current = null;
    setAudioLevels(IDLE_AUDIO_LEVELS);
  }, []);

  const startLevelsLoop = useCallback((analyser: AnalyserNode) => {
    stopLevelsLoop();
    analyserRef.current = analyser;
    const data = new Uint8Array(analyser.frequencyBinCount);

    const tick = () => {
      const node = analyserRef.current;
      if (!node) return;

      if (agentStateRef.current === 'listening' && inputOpenRef.current) {
        node.getByteFrequencyData(data);
        const step = Math.max(1, Math.floor(data.length / WAVEFORM_BAR_COUNT));
        const next: number[] = [];
        for (let i = 0; i < WAVEFORM_BAR_COUNT; i += 1) {
          let sum = 0;
          const start = i * step;
          const end = Math.min(start + step, data.length);
          for (let j = start; j < end; j += 1) sum += data[j];
          const avg = end > start ? sum / (end - start) / 255 : 0;
          next.push(Math.max(0.08, Math.min(1, avg * 1.4)));
        }
        setAudioLevels(next);
      } else {
        setAudioLevels(IDLE_AUDIO_LEVELS);
      }

      levelsRafRef.current = requestAnimationFrame(tick);
    };

    levelsRafRef.current = requestAnimationFrame(tick);
  }, [stopLevelsLoop]);

  const cleanupAudio = useCallback(() => {
    clearMicOpenTimer();
    stopSilencePump();
    stopLevelsLoop();
    workletNodeRef.current?.disconnect();
    sourceNodeRef.current?.disconnect();
    workletNodeRef.current = null;
    sourceNodeRef.current = null;
    micStreamRef.current?.getTracks().forEach((track) => track.stop());
    micStreamRef.current = null;
    if (audioContextRef.current) {
      audioContextRef.current.close().catch(() => {});
      audioContextRef.current = null;
    }
    playerRef.current = null;
    inputOpenRef.current = true;
  }, [clearMicOpenTimer, stopLevelsLoop, stopSilencePump]);

  const stop = useCallback(() => {
    connectInFlightRef.current = false;
    wsRef.current?.close();
    wsRef.current = null;
    cleanupAudio();
    connectionWasActiveRef.current = false;
    agentStateRef.current = 'idle';
    setConnectionState('idle');
    setAgentState('idle');
  }, [cleanupAudio]);

  const waitForPlaybackDrain = useCallback((): Promise<void> => {
    return new Promise((resolve) => {
      const startedAt = Date.now();
      const poll = () => {
        if (!playerRef.current?.hasPendingAudio()) {
          resolve();
          return;
        }
        if (Date.now() - startedAt >= DRAIN_TIMEOUT_MS) {
          resolve();
          return;
        }
        window.setTimeout(poll, 50);
      };
      poll();
    });
  }, []);

  const scheduleSessionEndFallback = useCallback(
    (metrics: VoiceAgentSessionMetrics, reason: 'max_questions' | 'max_warnings') => {
      clearEndBackupTimer();
      endBackupTimerRef.current = window.setTimeout(() => {
        if (sessionFinalizedRef.current || finalizeInProgressRef.current) return;
        void finalizeSessionRef.current(
          reason,
          undefined,
          metrics.question_count,
          metrics.warning_count
        );
      }, SESSION_END_FALLBACK_MS);
    },
    [clearEndBackupTimer]
  );

  const finalizeSession = useCallback(
    async (
      reason: string,
      endMessage?: string,
      questionCount?: number,
      warningCount?: number
    ) => {
      if (finalizeInProgressRef.current || sessionFinalizedRef.current) return;
      finalizeInProgressRef.current = true;
      clearEndBackupTimer();

      const endsWithoutFeedback = reason === 'user_request' || reason === 'max_warnings';

      if (!endsWithoutFeedback) {
        setPhase('finalizing');
        setInputOpen(false);
        agentStateRef.current = 'thinking';
        setAgentState('thinking');
      } else {
        setInputOpen(false);
      }

      let feedback: Record<string, unknown> | undefined;
      let conversationHistory: Array<{ role: string; content: string }> | undefined;

      try {
        if (!endsWithoutFeedback || reason === 'max_warnings') {
          await waitForPlaybackDrain();
        }

        const sid = sessionIdRef.current;
        if (sid) {
          try {
            const result = await voiceAgentApi.endInterview(sid, reason);
            if (!endsWithoutFeedback && result.status !== 'abandoned') {
              feedback = result.feedback;
              conversationHistory = result.conversation_history;
            }
          } catch (err) {
            console.warn('Voice agent end-interview failed:', err);
          }
        }

        if (!endsWithoutFeedback && !feedback && dbSessionIdRef.current) {
          const polled = await pollSessionFeedback(dbSessionIdRef.current);
          feedback = polled?.feedback;
          conversationHistory = conversationHistory ?? polled?.conversation_history;
        }

        setSessionEnded({
          reason,
          message: endMessage,
          question_count: questionCount,
          warning_count: warningCount,
          feedback,
          conversation_history: conversationHistory,
        });
        sessionFinalizedRef.current = true;

        if (endsWithoutFeedback) {
          setPhase('idle');
        } else if (feedback) {
          setPhase('feedback_ready');
        } else {
          setPhase('error');
          setErrorMessage('Feedback could not be loaded. You can view it later from Candidate Profile.');
        }
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Failed to finalize interview';
        if (!endsWithoutFeedback) {
          setErrorMessage(message);
          setPhase('error');
        } else {
          setPhase('idle');
        }
        setSessionEnded({
          reason,
          message: endMessage,
          question_count: questionCount,
          warning_count: warningCount,
        });
        sessionFinalizedRef.current = true;
      } finally {
        stop();
        finalizeInProgressRef.current = false;
      }
    },
    [clearEndBackupTimer, setInputOpen, stop, waitForPlaybackDrain]
  );

  finalizeSessionRef.current = finalizeSession;

  const handleServerEvent = useCallback(
    (message: Record<string, unknown>) => {
      if (sessionFinalizedRef.current && message.type !== 'SessionEnded') {
        return;
      }

      switch (message.type) {
        case 'ConversationText': {
          const role = message.role === 'assistant' ? 'agent' : 'user';
          appendTranscript(role, String(message.content ?? message.text ?? ''));
          break;
        }
        case 'InputGate':
          if (finalizeInProgressRef.current) break;
          if (message.open) {
            scheduleInputOpen();
          } else {
            clearMicOpenTimer();
            setInputOpen(false);
          }
          break;
        case 'UserStartedSpeaking':
          if (!inputOpenRef.current || finalizeInProgressRef.current) break;
          agentStateRef.current = 'listening';
          setAgentState('listening');
          break;
        case 'AgentThinking':
          clearMicOpenTimer();
          agentStateRef.current = 'thinking';
          setAgentState('thinking');
          setInputOpen(false);
          break;
        case 'AgentStartedSpeaking':
          clearMicOpenTimer();
          agentStateRef.current = 'speaking';
          setAgentState('speaking');
          setInputOpen(false);
          break;
        case 'AgentAudioDone':
          agentStateRef.current = 'speaking';
          setAgentState('speaking');
          scheduleInputOpen();
          break;
        case 'SessionMetrics': {
          const metrics = message as unknown as VoiceAgentSessionMetrics;
          setSessionMetrics(metrics);
          // Do not finalize on should_wrap_up here — wait for the candidate's answer
          // to be evaluated, agent closure speech, and SessionEnded from the server.
          if (metrics.should_end_now) {
            scheduleSessionEndFallback(metrics, 'max_warnings');
          } else if (metrics.should_wrap_up) {
            scheduleSessionEndFallback(metrics, 'max_questions');
          }
          break;
        }
        case 'SessionEnded':
          clearEndBackupTimer();
          void finalizeSession(
            String(message.reason ?? 'completed'),
            message.message as string | undefined,
            message.question_count as number | undefined,
            message.warning_count as number | undefined
          );
          break;
        case 'BackendError':
        case 'Error':
          setErrorMessage(
            String(message.message ?? message.description ?? 'Voice agent error')
          );
          break;
        default:
          break;
      }
    },
    [
      appendTranscript,
      clearEndBackupTimer,
      clearMicOpenTimer,
      finalizeSession,
      scheduleSessionEndFallback,
      scheduleInputOpen,
      setInputOpen,
    ]
  );

  const connectToSession = useCallback(
    async (params: VoiceAgentConnectParams) => {
      const currentState = connectionStateRef.current;
      if (
        connectInFlightRef.current ||
        currentState === 'connecting' ||
        currentState === 'connected'
      ) {
        return;
      }
      connectInFlightRef.current = true;

      if (wsRef.current) {
        const staleSocket = wsRef.current;
        wsRef.current = null;
        staleSocket.close();
      }

      setErrorMessage(null);
      setSessionEnded(null);
      setSessionMetrics(null);
      setTranscript([]);
      finalizeInProgressRef.current = false;
      sessionFinalizedRef.current = false;
      connectionWasActiveRef.current = false;
      clearEndBackupTimer();
      setPhase('idle');
      setConnectionState('connecting');

      const token = localStorage.getItem('authToken');
      if (!token) {
        connectInFlightRef.current = false;
        setErrorMessage('Not authenticated');
        setConnectionState('error');
        setPhase('error');
        return;
      }

      setSessionId(params.sessionId);
      setDbSessionId(params.dbSessionId ?? null);
      setMaxQuestions(params.maxQuestions ?? 7);
      if (params.firstQuestion) {
        appendTranscript('agent', params.firstQuestion);
      }

      try {
        const micStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: true,
            noiseSuppression: true,
            autoGainControl: true,
            channelCount: 1,
          },
        });
        micStreamRef.current = micStream;

        const audioContext = new AudioContext({ sampleRate: VOICE_AGENT_CONFIG.MIC_SAMPLE_RATE });
        audioContextRef.current = audioContext;
        if (audioContext.state === 'suspended') {
          await audioContext.resume();
        }
        playerRef.current = new PCMStreamPlayer(
          audioContext,
          VOICE_AGENT_CONFIG.AGENT_AUDIO_SAMPLE_RATE
        );

        const blob = new Blob([PCM_WORKLET_SOURCE], { type: 'application/javascript' });
        const workletUrl = URL.createObjectURL(blob);
        await audioContext.audioWorklet.addModule(workletUrl);
        URL.revokeObjectURL(workletUrl);

        const wsUrl = buildVoiceAgentWsUrl(params.wsPath, token);
        const ws = new WebSocket(wsUrl);
        ws.binaryType = 'arraybuffer';
        wsRef.current = ws;

        ws.onopen = () => {
          if (wsRef.current !== ws) return;
          connectInFlightRef.current = false;
          setConnectionState('connected');
          connectionWasActiveRef.current = true;
          setPhase('active');
          inputOpenRef.current = true;
          agentStateRef.current = 'listening';
          setAgentState('listening');

          const sourceNode = audioContext.createMediaStreamSource(micStream);
          const workletNode = new AudioWorkletNode(audioContext, 'pcm-worklet-processor');
          const analyser = audioContext.createAnalyser();
          analyser.fftSize = 256;
          analyser.smoothingTimeConstant = 0.65;
          workletNode.port.onmessage = (event) => {
            if (ws.readyState !== WebSocket.OPEN || !inputOpenRef.current) return;
            ws.send(event.data);
          };
          sourceNode.connect(workletNode);
          sourceNode.connect(analyser);
          sourceNodeRef.current = sourceNode;
          workletNodeRef.current = workletNode;
          startLevelsLoop(analyser);
        };

        ws.onmessage = (event) => {
          if (typeof event.data === 'string') {
            try {
              handleServerEvent(JSON.parse(event.data));
            } catch {
              // ignore malformed JSON
            }
          } else if (!finalizeInProgressRef.current) {
            clearMicOpenTimer();
            setInputOpen(false);
            playerRef.current?.queue(event.data);
            scheduleInputOpen();
          }
        };

        ws.onerror = () => {
          if (wsRef.current !== ws || connectionWasActiveRef.current) return;
          connectInFlightRef.current = false;
          setErrorMessage('Voice agent connection failed');
          setConnectionState('error');
          setPhase('error');
        };

        ws.onclose = () => {
          if (wsRef.current !== ws) return;
          connectInFlightRef.current = false;
          cleanupAudio();
          connectionWasActiveRef.current = false;
          agentStateRef.current = 'idle';
          setConnectionState((prev) => (prev === 'error' ? prev : 'idle'));
          setAgentState('idle');
        };
      } catch (err: unknown) {
        connectInFlightRef.current = false;
        const message = err instanceof Error ? err.message : 'Could not connect to voice interview';
        setErrorMessage(message);
        setConnectionState('error');
        setPhase('error');
        cleanupAudio();
        throw err;
      }
    },
    [
      appendTranscript,
      cleanupAudio,
      clearEndBackupTimer,
      clearMicOpenTimer,
      handleServerEvent,
      scheduleInputOpen,
      setInputOpen,
      startLevelsLoop,
    ]
  );

  const start = useCallback(
    async (config: VoiceAgentStartRequest) => {
      const startResult = await voiceAgentApi.startInterview(config);
      if (!startResult.success || !startResult.session_id || !startResult.ws_path) {
        throw new Error(startResult.error || startResult.message || 'Failed to start interview');
      }

      await connectToSession({
        sessionId: startResult.session_id,
        wsPath: startResult.ws_path,
        firstQuestion: startResult.first_question,
        maxQuestions: startResult.max_questions,
        dbSessionId: startResult.db_session_id,
      });
    },
    [connectToSession]
  );

  const endInterview = useCallback(async () => {
    if (finalizeInProgressRef.current || sessionFinalizedRef.current) return;
    finalizeInProgressRef.current = true;
    sessionFinalizedRef.current = true;
    clearEndBackupTimer();
    setInputOpen(false);

    try {
      const sid = sessionIdRef.current;
      if (sid) {
        await voiceAgentApi.endInterview(sid, 'user_request');
      }
    } catch (err) {
      console.warn('Voice agent abandon failed:', err);
    } finally {
      stop();
      setSessionEnded({ reason: 'user_request' });
      setPhase('idle');
      finalizeInProgressRef.current = false;
    }
  }, [clearEndBackupTimer, setInputOpen, stop]);

  useEffect(() => {
    return () => {
      clearEndBackupTimer();
      connectInFlightRef.current = false;
      const ws = wsRef.current;
      wsRef.current = null;
      if (ws && ws.readyState === WebSocket.OPEN) {
        ws.close();
      }
      cleanupAudio();
    };
  }, [cleanupAudio, clearEndBackupTimer]);

  return {
    connectionState,
    agentState,
    phase,
    transcript,
    sessionMetrics,
    sessionEnded,
    errorMessage,
    sessionId,
    dbSessionId,
    maxQuestions,
    audioLevels,
    start,
    connectToSession,
    stop,
    endInterview,
  };
}

export default useVoiceAgent;
